// Soft MIDI-style tones for Prybar, mirroring the Attention Card ritual. Pure
// Web Audio, no assets. The hold tones + resolve chord match HOLD_MS so pull
// feels identical to push. Unlocked on the first gesture (browsers require it).
const PENTA = [392.0, 440.0, 523.25, 587.33, 659.25, 783.99, 880.0, 1046.5];

let ctx: AudioContext | null = null;
let muted = false;

function ensure(): AudioContext | null {
  if (!ctx) {
    try {
      const Ctor =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function bell(freq: number, when = 0, dur = 0.5, peak = 0.16, attack = 0.012): void {
  const c = ctx;
  if (!c || muted) return;
  const t = c.currentTime + when;
  const osc = c.createOscillator();
  const g = c.createGain();
  const osc2 = c.createOscillator();
  const g2 = c.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  osc2.type = "triangle";
  osc2.frequency.value = freq * 2.001;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  g2.gain.setValueAtTime(0.0001, t);
  g2.gain.exponentialRampToValueAtTime(peak * 0.35, t + attack);
  g2.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.7);
  osc.connect(g).connect(c.destination);
  osc2.connect(g2).connect(c.destination);
  osc.start(t);
  osc.stop(t + dur + 0.05);
  osc2.start(t);
  osc2.stop(t + dur + 0.05);
}

// Pure-sine pad with a faintly detuned twin: the open chime breathes, not chimes.
function pad(freq: number, when = 0, dur = 1.5, peak = 0.08, attack = 0.12): void {
  const c = ctx;
  if (!c || muted) return;
  const t = c.currentTime + when;
  [0, 3.5].forEach((cents, i) => {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = "sine";
    osc.frequency.value = freq * 2 ** (cents / 1200);
    const pk = peak * (i === 0 ? 1 : 0.6);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(pk, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(c.destination);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  });
}

export const Sound = {
  unlock(): void {
    ensure();
  },
  setMuted(m: boolean): void {
    muted = m;
  },
  tick(i: number): void {
    ensure();
    bell(PENTA[Math.min(i, PENTA.length - 1)] ?? 392.0, 0, 0.42, 0.12);
  },
  resolve(): void {
    ensure();
    bell(523.25, 0, 1.4, 0.16);
    bell(659.25, 0.06, 1.4, 0.13);
    bell(783.99, 0.12, 1.6, 0.13);
    bell(261.63, 0, 1.8, 0.1);
  },
  // A warm ascending A major triad that blends into one resolved chord.
  open(): void {
    ensure();
    pad(440.0, 0, 1.5, 0.055, 0.05);
    pad(554.37, 0.07, 1.5, 0.05, 0.05);
    pad(659.25, 0.14, 1.7, 0.055, 0.05);
    pad(220.0, 0, 1.8, 0.025, 0.1);
  },
  // soft descending A, gentle dismiss
  cancel(): void {
    ensure();
    pad(329.63, 0, 0.55, 0.035, 0.05);
    pad(220.0, 0.08, 0.75, 0.03, 0.07);
  },
  // listening started: rising A to E over the low root
  wake(): void {
    ensure();
    pad(220.0, 0, 0.9, 0.022, 0.06);
    pad(440.0, 0, 0.7, 0.045, 0.04);
    pad(659.25, 0.09, 0.8, 0.04, 0.04);
  },
  // voice answer landed: settle E down to A
  done(): void {
    ensure();
    pad(220.0, 0, 1.0, 0.02, 0.07);
    pad(659.25, 0, 0.8, 0.04, 0.04);
    pad(440.0, 0.09, 0.95, 0.038, 0.05);
  },
  // spoken action needs a yes/no: open rising fourth, left unresolved
  ask(): void {
    ensure();
    pad(554.37, 0, 0.65, 0.04, 0.04);
    pad(739.99, 0.1, 0.75, 0.04, 0.04);
  },
};
